import { Grid, Skeleton, Tab, Tabs, Typography } from '@mui/material';
import { RefObject, forwardRef, useEffect, useImperativeHandle, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { BilhetePassagem } from 'src/models/LinkPagamento';

import { useUserContext } from '../../../contexts/User.context';
import { TipoTransacaoLista } from '../../../models/Carteira.model';
import { FiltroTransacoes } from '../../../models/FiltroTransacoes.model';
import {
  TIPO_TRANSACAO_PF,
  TIPO_TRANSACAO_PJ,
  TIPO_TRANSACAO_PJ_USER,
  Transacao
} from '../../../models/Transacao.model';
import { TipoUsuario } from '../../../models/Usuario';
import { CarteiraService } from '../../../services';
import { Colors } from '../../../shared/colors';
import useInfiniteScroll from '../../../shared/hooks/infiniteScroll';
import CardTransacao from '../../components/CardTransacao';
import DrawerDetalhesViagem from '../detalhes-viagem/DrawerDetalhesViagem';
import DrawerFiltrarTransacao from '../filtrar-transacoes/DrawerFiltrarTransacaoUsuarios';

interface Props {
  usuarioAssociadoEmpresa: boolean;
  tourRef?: RefObject<HTMLDivElement>;
  abasRef?: RefObject<HTMLDivElement>;
  exibirAlvoTour?: boolean;
}

export interface ListaTransacoesRef {
  atualizarTransacoes: () => void;
  abrirFiltro: () => void;
}

const ListarTransacoes = forwardRef<ListaTransacoesRef, Props>(
  ({ usuarioAssociadoEmpresa, tourRef, abasRef, exibirAlvoTour = true }, ref) => {
    const navigate = useNavigate();
    const { userClaim } = useUserContext();
    const usuarioPJ = userClaim === TipoUsuario.PESSOA_JURIDICA;

    const [openDrawer, setOpenDrawer] = useState(false);
    const [filtros, setFiltros] = useState<FiltroTransacoes>({});
    const [tab, setTab] = useState(TipoTransacaoLista.PESSOA_FISICA);
    const [bilhetesPassagem, setBilhetesPassagem] = useState<BilhetePassagem[] | null>(null);

    // Define qual função de load usar de acordo com o contexto (PF/PJ + aba)
    const loadFunction = useMemo(() => {
      if (usuarioPJ) {
        return CarteiraService.getTransacoes;
      }

      return tab === TipoTransacaoLista.PESSOA_FISICA
        ? CarteiraService.getTransacoesCarteiraPF
        : CarteiraService.getTransacoesCarteiraPJ;
    }, [usuarioPJ, tab]);

    const {
      refresh,
      hasNext,
      loading,
      lastElementRef,
      items: transacoes
    } = useInfiniteScroll<Transacao, FiltroTransacoes>({
      search: filtros,
      load: loadFunction
    });

    useImperativeHandle(
      ref,
      () => ({ atualizarTransacoes: refresh, abrirFiltro: () => setOpenDrawer(true) }),
      [refresh]
    );

    // Quando trocar a aba, zera os filtros
    useEffect(() => {
      setFiltros({});
      // o hook de infiniteScroll deve reagir ao search novo
    }, [tab]);

    const carregandoItens = loading && (hasNext || transacoes.length === 0);

    const filtroDrawerPF =
      tab === TipoTransacaoLista.PESSOA_FISICA ? TIPO_TRANSACAO_PF : TIPO_TRANSACAO_PJ_USER;

    const filtrosDrawer = usuarioPJ ? TIPO_TRANSACAO_PJ : filtroDrawerPF;

    const handleChangeTab = (_: React.SyntheticEvent, value: TipoTransacaoLista) => {
      setTab(value);
    };

    const closeDrawerFiltro = (filtroTransacoes?: FiltroTransacoes) => {
      setOpenDrawer(false);
      if (filtroTransacoes) {
        setFiltros(filtroTransacoes);
      }
    };

    const handleOpenDetalhes = async (transacao: Transacao) => {
      const bilhetes = transacao.bilhetes?.length
        ? transacao.bilhetes
        : await CarteiraService.getBilhetesTransacao(transacao.idTransacao);

      setBilhetesPassagem(bilhetes);
    };

    return (
      <>
        {!usuarioPJ && usuarioAssociadoEmpresa && (
          <Tabs ref={abasRef} value={tab} onChange={handleChangeTab} variant="fullWidth">
            <Tab label="Transações pessoais" value={TipoTransacaoLista.PESSOA_FISICA} />
            <Tab label="Transações empresa" value={TipoTransacaoLista.PESSOA_JURIDICA} />
          </Tabs>
        )}

        {transacoes.map((transacao, index, arr) => {
          const lastElement = arr.length === index + 1;
          const refItem = lastElement && hasNext ? lastElementRef : null;

          const card = (
            <CardTransacao
              walletLayout
              onClick={() => handleOpenDetalhes(transacao)}
              navigateToRecibo={() => navigate(`/recibo-preview/${transacao.idTransacao}`)}
              transacao={transacao}
            />
          );

          return (
            <div ref={refItem} key={transacao.idTransacao}>
              {index === 0 && exibirAlvoTour ? (
                <div ref={tourRef} data-tour-pj="wallet-transactions">
                  {card}
                </div>
              ) : (
                card
              )}
            </div>
          );
        })}

        <Grid item xs={12}>
          {carregandoItens && (
            <Grid container mt={2} gap={2}>
              {Array.from({ length: 5 }, (_, index) => (
                <Skeleton key={index} variant="rounded" width="100%" height={70} />
              ))}
            </Grid>
          )}

          {!loading && transacoes.length === 0 && (
            <div
              ref={exibirAlvoTour ? tourRef : undefined}
              data-tour-pj={exibirAlvoTour ? 'wallet-transactions' : undefined}
            >
              <Typography
                variant="body1"
                fontWeight={500}
                color={Colors.gray1}
                textAlign="center"
                mt={3}
              >
                Não há dados a serem exibidos
              </Typography>
            </div>
          )}
        </Grid>

        <DrawerFiltrarTransacao
          open={openDrawer}
          onClose={closeDrawerFiltro}
          onCancel={() => setOpenDrawer(false)}
          tipoTransacoes={filtrosDrawer}
          tab={tab}
        />

        <DrawerDetalhesViagem
          bilhetes={bilhetesPassagem}
          onClose={() => setBilhetesPassagem(null)}
        />
      </>
    );
  }
);

ListarTransacoes.displayName = 'ListaTransacoes';
export default ListarTransacoes;
